import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { endpoints } from '../utils/apiClient';
import { useAuth } from '../context/AuthContext';

const CreateTournament = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    game: '',
    error: '',
    prize: '',
    date: '',
    maxParticipants: '',
    thumbnail: '',
    description: '',
    rules: [''],
    schedule: [{ phase: '', date: '', status: 'Upcoming' }],
    prizes: [{ place: '', amount: '', extras: '' }]
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRuleChange = (index, value) => {
    const newRules = [...formData.rules];
    newRules[index] = value;
    setFormData(prev => ({
      ...prev,
      rules: newRules
    }));
  };

  const addRule = () => {
    setFormData(prev => ({
      ...prev,
      rules: [...prev.rules, '']
    }));
  };

  const handleScheduleChange = (index, field, value) => {
    const newSchedule = [...formData.schedule];
    newSchedule[index] = {
      ...newSchedule[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      schedule: newSchedule
    }));
  };

  const addSchedulePhase = () => {
    setFormData(prev => ({
      ...prev,
      schedule: [...prev.schedule, { phase: '', date: '', status: 'Upcoming' }]
    }));
  };

  const handlePrizeChange = (index, field, value) => {
    const newPrizes = [...formData.prizes];
    newPrizes[index] = {
      ...newPrizes[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      prizes: newPrizes
    }));
  };

  const addPrize = () => {
    setFormData(prev => ({
      ...prev,
      prizes: [...prev.prizes, { place: '', amount: '', extras: '' }]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setFormData(prev => ({ ...prev, error: '' }));

      // Check if user is authenticated
      if (!isAuthenticated || !user) {
        setFormData(prev => ({
          ...prev,
          error: 'You must be logged in to create a tournament'
        }));
        return;
      }

      // Format the data with proper date formatting
      const formattedData = {
        ...formData,
        maxParticipants: parseInt(formData.maxParticipants),
        // Ensure date is in ISO format
        date: new Date(formData.date).toISOString(),
        rules: formData.rules.filter(rule => rule.trim() !== ''),
        schedule: formData.schedule.map(phase => ({
          ...phase,
          // Ensure schedule dates are in ISO format
          date: new Date(phase.date).toISOString(),
          status: phase.status || 'Upcoming'
        })),
        prizes: formData.prizes.filter(prize =>
          prize.place.trim() !== '' ||
          prize.amount.trim() !== '' ||
          prize.extras.trim() !== ''
        ).map(prize => ({
          place: prize.place.trim(),
          amount: prize.amount.trim(),
          extras: prize.extras.trim()
        }))
      };

      console.log('Submitting tournament data:', formattedData);
      const response = await api.post(endpoints.tournaments.create, formattedData);
      console.log('Tournament creation response:', response);

      // Improved response handling
      if (response && response._id) {
        // Direct response contains tournament
        navigate(`/tournament/${response._id}`);
      } else if (response && response.tournament && response.tournament._id) {
        // Response contains tournament in a nested object
        navigate(`/tournament/${response.tournament._id}`);
      } else if (response && response.data && response.data._id) {
        // Response might be wrapped in a data property
        navigate(`/tournament/${response.data._id}`);
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (error) {
      console.error('Error creating tournament:', error);
      // More detailed error logging
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
      }

      // Set a more descriptive error message
      const errorMessage = error.response?.data?.error || error.message || 'Failed to create tournament';
      setFormData(prev => ({
        ...prev,
        error: errorMessage
      }));
    }
  };

  return (
    <div className="p-6 text-white">
      <h1 className="text-3xl font-bold mb-8">Create Tournament</h1>

      {formData.error && (
        <div className="bg-red-500 text-white p-4 rounded-lg mb-6">
          {formData.error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <div className="bg-gray-900 rounded-xl p-6">
          <h2 className="text-xl font-semibold mb-4">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2">Tournament Title</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="w-full bg-gray-800 rounded-lg p-3 text-white"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Game</label>
              <input
                type="text"
                name="game"
                value={formData.game}
                onChange={handleInputChange}
                className="w-full bg-gray-800 rounded-lg p-3 text-white"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Prize Pool</label>
              <input
                type="text"
                name="prize"
                value={formData.prize}
                onChange={handleInputChange}
                className="w-full bg-gray-800 rounded-lg p-3 text-white"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Tournament Date</label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                className="w-full bg-gray-800 rounded-lg p-3 text-white"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Max Participants</label>
              <input
                type="number"
                name="maxParticipants"
                value={formData.maxParticipants}
                onChange={handleInputChange}
                className="w-full bg-gray-800 rounded-lg p-3 text-white"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Thumbnail URL</label>
              <input
                type="url"
                name="thumbnail"
                value={formData.thumbnail}
                onChange={handleInputChange}
                className="w-full bg-gray-800 rounded-lg p-3 text-white"
                required
              />
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="bg-gray-900 rounded-xl p-6">
          <h2 className="text-xl font-semibold mb-4">Description</h2>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            className="w-full bg-gray-800 rounded-lg p-3 text-white h-32"
            required
          />
        </div>

        {/* Rules */}
        <div className="bg-gray-900 rounded-xl p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Rules</h2>
            <button
              type="button"
              onClick={addRule}
              className="px-4 py-2 bg-purple-600 rounded-lg hover:bg-purple-700"
            >
              Add Rule
            </button>
          </div>
          <div className="space-y-4">
            {formData.rules.map((rule, index) => (
              <input
                key={index}
                type="text"
                value={rule}
                onChange={(e) => handleRuleChange(index, e.target.value)}
                className="w-full bg-gray-800 rounded-lg p-3 text-white"
                placeholder={`Rule ${index + 1}`}
                required
              />
            ))}
          </div>
        </div>

        {/* Schedule */}
        <div className="bg-gray-900 rounded-xl p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Schedule</h2>
            <button
              type="button"
              onClick={addSchedulePhase}
              className="px-4 py-2 bg-purple-600 rounded-lg hover:bg-purple-700"
            >
              Add Phase
            </button>
          </div>
          <div className="space-y-4">
            {formData.schedule.map((phase, index) => (
              <div key={index} className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  value={phase.phase}
                  onChange={(e) => handleScheduleChange(index, 'phase', e.target.value)}
                  className="bg-gray-800 rounded-lg p-3 text-white"
                  placeholder="Phase Name"
                  required
                />
                <input
                  type="date"
                  value={phase.date}
                  onChange={(e) => handleScheduleChange(index, 'date', e.target.value)}
                  className="bg-gray-800 rounded-lg p-3 text-white"
                  placeholder="Phase Date"
                  required
                />
              </div>
            ))}
          </div>
        </div>

        {/* Prizes */}
        <div className="bg-gray-900 rounded-xl p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Prizes</h2>
            <button
              type="button"
              onClick={addPrize}
              className="px-4 py-2 bg-purple-600 rounded-lg hover:bg-purple-700"
            >
              Add Prize
            </button>
          </div>
          <div className="space-y-4">
            {formData.prizes.map((prize, index) => (
              <div key={index} className="grid grid-cols-3 gap-4">
                <input
                  type="text"
                  value={prize.place}
                  onChange={(e) => handlePrizeChange(index, 'place', e.target.value)}
                  className="bg-gray-800 rounded-lg p-3 text-white"
                  placeholder="Place"
                />
                <input
                  type="text"
                  value={prize.amount}
                  onChange={(e) => handlePrizeChange(index, 'amount', e.target.value)}
                  className="bg-gray-800 rounded-lg p-3 text-white"
                  placeholder="Amount"
                />
                <input
                  type="text"
                  value={prize.extras}
                  onChange={(e) => handlePrizeChange(index, 'extras', e.target.value)}
                  className="bg-gray-800 rounded-lg p-3 text-white"
                  placeholder="Extras"
                />
              </div>
            ))}
          </div>
        </div>

        <button
          type="submit"
          className="w-full py-4 bg-purple-600 rounded-xl font-semibold hover:bg-purple-700"
        >
          Create Tournament
        </button>
      </form>
    </div>
  );
};

export default CreateTournament;
